'use client'

import AmenitiesFilter from '@/app/components/ResultsFilters/AmenitiesFilter'
import FilterButtons from '@/app/components/ResultsFilters/FilterButtons'
import GuestRatingFilter from '@/app/components/ResultsFilters/GuestRatingFilter'
import PriceFilter from '@/app/components/ResultsFilters/PriceFilter'
import StayOptionsFilter from '@/app/components/ResultsFilters/StayOptionsFilter'
import { ViewOnMap } from '@/app/components/ResultsFilters/ViewOnMap'
import {
	DEFAULT_CURRENCY,
	FACILITIES,
	FILTER_TYPES,
	INIT_DATA_STORAGE_KEY,
	STAY_OPTIONS,
	XL_BREAKPOINT,
} from '@/app/constants'
import {
	emitRpFilterAmenitiesSelectGAEvent,
	emitRpFilterGuestRatingSelectGAEvent,
	emitRpFilterPopularSelectGAEvent,
	emitRpFilterPriceSelectGAEvent,
	emitRpFilterStarsSelectGAEvent,
	emitRpFilterStaySelectGAEvent,
} from '@/app/ga-event-helpers'
import { useMapView } from '@/app/hooks/useMapView'
import { DestPagesProps, Filters, FiltersCountMap, UserData } from '@/app/types'
import { HotelContentAccommodationTypeEnum } from '@/app/types/skyscanner-wrapper-api'
import { isApartment, isRtlLanguage, smoothScrollTo } from '@/app/utils'
import { useLocalStorage, useMediaQuery } from '@uidotdev/usehooks'
import cn from 'classnames'
import { useTranslations } from 'next-intl'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import StarRatingFilter from './StarRatingFilter'
import { STARS } from '../../constants'
import PopularFilters from './PopularFilters'

export interface ResultsFiltersProps {
	pageProps: DestPagesProps
	defaultValueStars?: number
	defaultValueRating?: number
	availableRates: { min: number; max: number }
	onClose?: () => void
	hideButtons?: boolean
	onStateChange?: (state: {
		localValue: Filters
		applyFilters: () => void
		handleReset: () => void
		countActiveFilters: (filters: Filters) => number
	}) => void
}
const getMapSearchParams = () => {
	// Update URL with new polygon, lat, and lng parameters
	const currentUrl = new URL(window.location.href)
	const newSearchParams = new URLSearchParams(currentUrl.search)
	const zoom = newSearchParams.get('zoom')
	const lat = newSearchParams.get('lat')
	const lng = newSearchParams.get('lng')
	return { ...(lat && { lat }), ...(lng && { lng }), ...(zoom && { zoom }) }
}
const ResultsFilters: FC<ResultsFiltersProps> = memo(({
	pageProps,
	defaultValueStars = 0,
	defaultValueRating = 0,
	availableRates,
	onClose,
	hideButtons = false,
	onStateChange,
}) => {
	const t = useTranslations('Keywords')
	const router = useRouter()
	const [isOpen, setIsOpen] = useState(false)
	const [isDirty, setIsDirty] = useState(false)
	const query = useSearchParams()
	const [value, setValue] = useState<Filters>({
		stars: [],
		rating: [],
		facilities: [],
		stayOptions: [],
		priceRange: [availableRates.min, availableRates.max],
	})
	const filterRef = useRef<HTMLDivElement>(null)
	const isDesktop = useMediaQuery(XL_BREAKPOINT)
	const params = useParams<DestPagesProps['params']>()
	const selectedLanguage = params.lang
	const [showAllFacilities, setShowAllFacilities] = useState(false)
	const [showAllStayOptions, setShowAllStayOptions] = useState(false)
	const filterBottomRef = useRef<HTMLDivElement>(null)
	const [isFilterSticky, setIsFilterSticky] = useState(false)
	const [isFilterUpdate, setIsFilterUpdate] = useState(false)
	const { isListView, isFullScreenMap } = useMapView()
	const [initData, setItem] = useLocalStorage<UserData>(INIT_DATA_STORAGE_KEY)
	const userCurrency = initData?.selectedCurrency
	const currency =
		query.get('currency') || userCurrency?.abbreviation || DEFAULT_CURRENCY

	const toggleFacilitiesView = () => {
		setShowAllFacilities(!showAllFacilities)
	}

	const toggleStayOptionsView = () => {
		setShowAllStayOptions(!showAllStayOptions)
	}

	useEffect(() => {
		const initialValue = getValue()
		setValue(initialValue)
		setLocalValue(initialValue)
		setIsDirty(false)
	}, [pageProps.searchParams, availableRates])

	const parseParamToNumbers = (param: string | undefined): number[] => {
		if (!param || param === 'undefined') return []
		return param
			.split('|')
			.map(Number)
			.filter((n) => !isNaN(n))
	}

	const parseRatingParam = (param: string | undefined): number[] => {
		if (!param || param === 'undefined') return []
		const numbers = parseParamToNumbers(param)
		return numbers.length > 0 ? [Math.min(...numbers)] : []
	}

	const getValue = (): Filters => {
		const {
			s: starsParam,
			gr: ratingParam,
			mn: minPrice,
			mx: maxPrice,
			lmn: lMinPrice,
			lmx: lMaxPrice,
			fa: facilitiesParam,
			p: stayOptionsParam,
			t: themesParam,
		} = pageProps.searchParams

		const stars = parseParamToNumbers(starsParam)
		const rating = parseRatingParam(ratingParam)
		const facilities = parseParamToNumbers(facilitiesParam)
		// Filter out invalid stay option IDs (only allow IDs from STAY_OPTIONS)
		const validStayOptionIds = new Set(STAY_OPTIONS.map((option) => option.id))
		const stayOptions = parseParamToNumbers(stayOptionsParam).filter((id) =>
			validStayOptionIds.has(id)
		)
		const hotelStyles = parseParamToNumbers(themesParam)

		let priceRangeLimits: [number, number] | undefined
		if (lMinPrice && lMaxPrice) {
			priceRangeLimits = [lMinPrice, lMaxPrice]
		}

		let priceRange: [number, number]

		if (minPrice || maxPrice) {
			const min = minPrice ? +minPrice : availableRates.min
			const max = maxPrice ? +maxPrice : availableRates.max
			priceRange = [min, max]
		} else {
			priceRange = [availableRates.min, availableRates.max]
		}

		return {
			stars,
			rating,
			facilities,
			stayOptions,
			hotelStyles,
			priceRange,
			priceRangeLimits
		}
	}

	const [localValue, setLocalValue] = useState<Filters>({
		stars: [],
		rating: [],
		facilities: [],
		stayOptions: [],
		priceRange: [availableRates.min, availableRates.max],
	})

	const applyFilters = () => {
		setIsFilterUpdate(true)
		setValue(localValue)
		const { s, gr, page, mn, mx, lmn, lmx, fa, p, ...restParams } = pageProps.searchParams
		const newQuery = generateNewQuery(localValue, restParams)
		updateURL(newQuery)
		setIsDirty(false)
		onClose && onClose()
	}

	const handleReset = () => {
		// emitFilterResetClickGAEvent()
		const resetValue: Filters = {
			stars: [],
			rating: [],
			priceRange: [availableRates.min, availableRates.max],
			facilities: [],
			stayOptions: [],
			hotelStyles: [],
		}
		setValue(resetValue)
		setLocalValue(resetValue)
		setIsDirty(false)
		const { s, gr, page, mn, mx, lmn, lmx, fa, p, t, ...restParams } =
			pageProps.searchParams

		const searchParams = new URLSearchParams(
			Object.fromEntries(
				Object.entries(restParams).map(([key, value]) => [
					key,
					Array.isArray(value) ? value.join(',') : value,
				])
			)
		)
		const newUrl = `${window.location.pathname}?${searchParams.toString()}`
		router.push(newUrl)
	}

	const updateLocalValue = (newPartialValue: Partial<Filters>) => {
		setLocalValue((prev: Filters) => ({ ...prev, ...newPartialValue }))
		setIsDirty(true)

		if (isDesktop) {
			setIsFilterUpdate(true)
			setValue((prev: Filters) => ({ ...prev, ...newPartialValue }))
		}
	}

	const handleFacilityChange = (
		facilityIds: number | number[],
		name?: string
	) => {
		// Normalize to array
		const ids = Array.isArray(facilityIds) ? facilityIds : [facilityIds]

		// Check if any ID in the group is already selected
		const isAnySelected = ids.some((id) => localValue.facilities.includes(id))

		let newFacilities: number[]
		if (isAnySelected) {
			// Remove all IDs in the group
			newFacilities = localValue.facilities.filter(
				(f: number) => !ids.includes(f)
			)
		} else {
			// Add all IDs in the group
			newFacilities = [...localValue.facilities, ...ids]
		}

		updateLocalValue({ facilities: newFacilities })

		if (name) {
			// Name is provided, emit GA event for amenity selection (use first ID for tracking)
			emitRpFilterAmenitiesSelectGAEvent(ids[0], !isAnySelected, name)
		}

		if (isDesktop) {
			smoothScrollTo(0, 0)
		}

		return !isAnySelected
	}

	const handleStayOptionChange = (optionId: number, name?: string) => {
		const isNewValue = !localValue.stayOptions.includes(optionId)
		let newOptions = isNewValue
			? [...localValue.stayOptions, optionId]
			: localValue.stayOptions.filter((f) => f !== optionId)

		if (name) {
			emitRpFilterStaySelectGAEvent(name, isNewValue)
		}

		updateLocalValue({ stayOptions: newOptions })

		if (isDesktop) {
			smoothScrollTo(0, 0)
		}

		return isNewValue
	}

	const handleRatingChange = (ratingValue: number, name?: string) => {
		const newRating = localValue.rating.includes(ratingValue)
			? [] // Remove the filter completely if it's already selected
			: [ratingValue] // Add the new rating

		updateLocalValue({ rating: newRating })

		if (name) {
			emitRpFilterGuestRatingSelectGAEvent(name, newRating.length > 0)
		}

		if (isDesktop) {
			smoothScrollTo(0, 0)
		}

		return newRating.length > 0
	}

	const handleStarChange = (star: number, track?: boolean) => {
		const isNewValue = !localValue.stars.includes(star)
		const newStars = isNewValue
			? [...localValue.stars, star]
			: localValue.stars.filter((f) => f !== star)

		updateLocalValue({ stars: newStars })

		if (track) {
			emitRpFilterStarsSelectGAEvent(star, isNewValue)
		}

		if (isDesktop) {
			smoothScrollTo(0, 0)
		}

		return isNewValue
	}

	const handlePriceRangeChange = (newValues: [number, number]) => {
		const newLimits:[number, number]  = localValue.priceRangeLimits ?
			[
				Math.min(availableRates.min, localValue.priceRangeLimits[0] || Number.MAX_VALUE),
				Math.max(availableRates.max, localValue.priceRangeLimits[1] || Number.MIN_VALUE),
			]
			: [availableRates.min, availableRates.max]
		
		updateLocalValue({
			priceRange: newValues,
			priceRangeLimits: newLimits
		})
			
		emitRpFilterPriceSelectGAEvent(newValues, currency)

		if (isDesktop) {
			smoothScrollTo(0, 0)
		}
	}

	const handlePopularFilterChange = (filter: {
		id: number
		type: string
		name: string
	}) => {
		let isNewValue = false
		switch (filter.type) {
			case FILTER_TYPES.STARS:
				isNewValue = handleStarChange(Number(filter.id))
				break
			case FILTER_TYPES.STAY_OPTIONS:
				isNewValue = handleStayOptionChange(Number(filter.id))
				break
			case FILTER_TYPES.RATING:
				isNewValue = handleRatingChange(Number(filter.id))
				break
			case FILTER_TYPES.FACILITIES:
				isNewValue = handleFacilityChange(Number(filter.id))
				break
			default:
				console.log('Unknown filter type')
		}

		emitRpFilterPopularSelectGAEvent(filter.name, isNewValue)
	}

	const generateNewQuery = (currentValue: Filters, restParams: any) => {
		// Filter out invalid stay option IDs (only allow IDs from STAY_OPTIONS)
		const validStayOptionIds = new Set(STAY_OPTIONS.map((option) => option.id))
		const filteredStayOptions = currentValue.stayOptions.filter((id) =>
			validStayOptionIds.has(id)
		)

		// For facilities with OR logic groups, send all IDs from each selected group in sequence
		const facilityIdsToSend: number[] = []

		currentValue.facilities.forEach((facilityId) => {
			// Find which group this facility ID belongs to
			const facility = FACILITIES.find((facility) => facility.id === facilityId)
			if (facility) {
				facilityIdsToSend.push(facility.id)
			}
		})

		return {
			...restParams,
			...(currentValue.stars.length > 0
				? { s: currentValue.stars.join('|') }
				: {}),
			...(currentValue.rating.length > 0 && currentValue.rating[0] !== 0
				? {
						gr: Array.from(
							{ length: 11 - Math.min(...currentValue.rating) },
							(_, i) => i + Math.min(...currentValue.rating)
						).join('|'),
				  }
				: {}),
			...(facilityIdsToSend.length > 0
				? { fa: facilityIdsToSend.join('|') }
				: {}),
			// Only include p parameter if stay options are selected
			...(filteredStayOptions.length > 0
				? { p: filteredStayOptions.join('|') }
				: {}),
			...(currentValue.hotelStyles?.length
				? { t: currentValue.hotelStyles.join('|') }
				: {}),
			...(currentValue.priceRange
				? {
						...(Math.abs(currentValue.priceRange[0] - availableRates.min) > 1 &&
							currentValue.priceRange[0] > 0 && {
								mn: Math.round(currentValue.priceRange[0]).toString(),
							}),
						...(Math.abs(currentValue.priceRange[1] - availableRates.max) > 1 &&
							currentValue.priceRange[1] > 0 && {
								mx: Math.round(currentValue.priceRange[1]).toString(),
							}),
				  }
				: {}),
			...(currentValue.priceRangeLimits
				? { lmn: currentValue.priceRangeLimits[0], lmx: currentValue.priceRangeLimits[1] }
				: {}),
			...getMapSearchParams(),
		}
	}

	const updateURL = useCallback(
		(newQuery: any) => {
			if (!isFilterUpdate) return

			const searchParams = new URLSearchParams()

			Object.entries(newQuery).forEach(([key, value]) => {
				if (Array.isArray(value)) {
					value.forEach((item) => searchParams.append(key, item))
				} else {
					searchParams.append(key, value as string)
				}
			})

			const newUrl = `${window.location.pathname}?${searchParams.toString()}`
			router.push(newUrl, { scroll: false })

			setIsFilterUpdate(false)
		},
		[isFilterUpdate]
	)

	useEffect(() => {
		if (isFilterUpdate) {
			const { s, gr, page, mn, mx, fa, t, p, ap, ...restParams } =
				pageProps.searchParams
			const newQuery = generateNewQuery(value, restParams)
			updateURL(newQuery)
		}
	}, [value, pageProps.searchParams, isFilterUpdate])

	const countActiveFilters = (filters: Filters): number => {
		const { stars, rating, facilities, stayOptions, hotelStyles, priceRange } =
			filters
		const starsWithoutZero = stars.filter((r) => r !== 0)
		const ratingWithoutZero = rating.filter((r) => r !== 0)

		// Count facilities by group, not by individual IDs
		const facilityGroups = new Set<number>()
		facilities.forEach((facilityId) => {
			const facility = FACILITIES.find((facility) => facility.id === facilityId)
			if (facility) {
				facilityGroups.add(facility.id)
			}
		})

		let count =
			starsWithoutZero.length +
			ratingWithoutZero.length +
			facilityGroups.size +
			stayOptions.length +
			(hotelStyles?.length || 0)

		// Count price range filter
		if (priceRange && availableRates) {
			if (
				Math.abs(priceRange[0] - availableRates.min) > 1 ||
				Math.abs(priceRange[1] - availableRates.max) > 1
			) {
				count += 1
			}
		}

		return count
	}

	const options = {
		threshold: 0,
		rootMargin: '-116px 0px 0px 0px',
	}

	const observerCallback = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			const filterEntry = entries.find(
				(entry) => entry.target === filterRef.current
			)
			const bottomEntry = entries.find(
				(entry) => entry.target === filterBottomRef.current
			)

			if (filterEntry && bottomEntry) {
				if (
					!filterEntry.isIntersecting &&
					filterEntry.boundingClientRect.top < 0
				) {
					setIsFilterSticky(true)
				} else if (bottomEntry.isIntersecting) {
					setIsFilterSticky(false)
				}
			}
		},
		[]
	)

	useEffect(() => {
		if (isDesktop) {
			const observer = new IntersectionObserver(observerCallback, options)

			const currentFilterRef = filterRef.current
			const currentFilterBottomRef = filterBottomRef.current

			if (currentFilterRef) {
				observer.observe(currentFilterRef)
			}

			if (currentFilterBottomRef) {
				observer.observe(currentFilterBottomRef)
			}

			return () => {
				if (currentFilterRef) {
					observer.unobserve(currentFilterRef)
				}
				if (currentFilterBottomRef) {
					observer.unobserve(currentFilterBottomRef)
				}
			}
		}
	}, [isDesktop, observerCallback, filterRef.current, filterBottomRef.current])

	useEffect(() => {
		if (onStateChange) {
			onStateChange({
				localValue,
				applyFilters,
				handleReset,
				countActiveFilters,
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localValue, onStateChange])

	return (
		<div
			className={cn('hotel-list-filters select-none z-18', {
				isListView,
				isFullScreenMap,
			})}
			ref={filterRef}
		>
			<div className="hotel-list-filters__container">
				<div className="filters max-xl:max-w-screen">
					<ViewOnMap />
					{/* <PopularFilters
						localValue={localValue}
						handlePopularFilterChange={handlePopularFilterChange}
						isFilterSticky={isFilterSticky}
					/> */}
					<PriceFilter
						localValue={localValue}
						availableRates={availableRates}
						handlePriceRangeChange={handlePriceRangeChange}
					/>
					<StayOptionsFilter
						localValue={localValue}
						handleStayOptionChange={handleStayOptionChange}
						showAllStayOptions={showAllStayOptions}
						toggleStayOptionsView={toggleStayOptionsView}
						availableFilters={STAY_OPTIONS}
					/>
					{/* <GuestRatingFilter
						localValue={localValue}
						handleRatingChange={handleRatingChange}
						availableFilters={availableFilters?.guestRatings}
					/> */}
					<StarRatingFilter
						localValue={localValue}
						handleStarChange={handleStarChange}
						availableFilters={STARS}
					/>
					<AmenitiesFilter
						localValue={localValue}
						handleFacilityChange={handleFacilityChange}
						showAllFacilities={showAllFacilities}
						toggleFacilitiesView={toggleFacilitiesView}
						availableFilters={FACILITIES}
					/>
					<div ref={filterBottomRef} style={{ height: '1px', width: '100%' }} />
				</div>

				{!isDesktop && !hideButtons && (
					<FilterButtons
						localValue={localValue}
						handleReset={handleReset}
						applyFilters={applyFilters}
						countActiveFilters={countActiveFilters}
					/>
				)}
			</div>
		</div>
	)
})
ResultsFilters.displayName = 'ResultsFilters'

export default ResultsFilters
